"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Bell, CheckCheck } from "lucide-react";
import { useLanguage } from "@/lib/i18n/context";
import { SeverityDot, formatRelativeTime, type InboxItem } from "./notification-ui";

const POLL_MS = 60_000;

export function NotificationBell() {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try {
      const res = await fetch("/api/notifications/inbox/count", { cache: "no-store" });
      if (res.ok) setUnread((await res.json()).unread ?? 0);
    } catch {
      // Offline or signed out: keep the last known count.
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/notifications/inbox?limit=8&lang=${language}`, { cache: "no-store" });
      if (res.ok) setItems((await res.json()).items ?? []);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    void refreshCount();
    const timer = setInterval(() => { if (document.visibilityState === "visible") void refreshCount(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    void loadItems();
    const onClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, loadItems]);

  const markRead = async (body: { ids?: string[]; all?: boolean }) => {
    await fetch("/api/notifications/inbox/read", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const now = new Date().toISOString();
    setItems((current) => current.map((item) => (body.all || body.ids?.includes(item.id) ? { ...item, readAt: item.readAt ?? now } : item)));
    void refreshCount();
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={t("notifications.bell.title")}
        aria-expanded={open}
        className="relative p-2 rounded-xl bg-surface-muted border border-themed-border text-themed-textSecondary hover:text-themed-textPrimary hover:border-themed-borderAccent transition-colors"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-[1.1rem] text-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-themed-border bg-surface-card shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-themed-borderSubtle">
            <div>
              <p className="text-sm font-semibold text-themed-textPrimary">{t("notifications.bell.title")}</p>
              {unread > 0 && <p className="text-[11px] text-themed-textMuted">{t("notifications.bell.unread", { count: unread })}</p>}
            </div>
            {unread > 0 && (
              <button type="button" onClick={() => void markRead({ all: true })} className="flex items-center gap-1 text-[11px] font-semibold text-[var(--accent-primary)] hover:underline">
                <CheckCheck className="w-3.5 h-3.5" /> {t("notifications.bell.markAllRead")}
              </button>
            )}
          </div>
          <div className="max-h-[26rem] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="p-6 text-center text-xs text-themed-textMuted">…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-xs text-themed-textMuted">{t("notifications.bell.empty")}</div>
            ) : (
              items.map((item) => {
                const content = (
                  <div className={`flex gap-3 px-4 py-3 border-b border-themed-borderSubtle hover:bg-surface-cardHover transition-colors ${item.readAt ? "opacity-70" : ""}`}>
                    <SeverityDot severity={item.severity} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs text-themed-textPrimary truncate ${item.readAt ? "" : "font-semibold"}`}>{item.title}</p>
                      <p className="text-[11px] text-themed-textSecondary line-clamp-2">{item.body}</p>
                      <p className="text-[10px] text-themed-textMuted mt-0.5">{formatRelativeTime(item.createdAt, language)}</p>
                    </div>
                  </div>
                );
                return item.url ? (
                  <Link key={item.id} href={item.url} onClick={() => { if (!item.readAt) void markRead({ ids: [item.id] }); setOpen(false); }}>{content}</Link>
                ) : (
                  <button key={item.id} type="button" className="w-full text-left" onClick={() => { if (!item.readAt) void markRead({ ids: [item.id] }); }}>{content}</button>
                );
              })
            )}
          </div>
          <Link href="/notifications" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-center text-xs font-semibold text-[var(--accent-primary)] hover:bg-surface-cardHover">
            {t("notifications.bell.viewAll")}
          </Link>
        </div>
      )}
    </div>
  );
}
